import { useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { useContacts } from '../../context/ContactContext';
import { useChatPermissions } from '../../hooks/useChatPermissions';
import {
  createDirectMessageSecure,
  createGroupChat
} from '../../services/chatService';
import { db } from '../../config/firebase';
import { searchUsers } from '../../services/userSearchService';

/**
 * NewChatModal - Create new DM or Group chat
 */

export default function NewChatModal({ onClose }) {
  const { user, userProfile } = useAuth();
  const { selectChat, chats } = useChat();
  const { contacts, checkCanDM } = useContacts();
  const {
    canUseGroupChats,
    canCreateGroupChat,
    getUpgradePrompt,
    userId,
    userName
  } = useChatPermissions();

  const [mode, setMode] = useState('dm'); // 'dm' or 'group'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  // Search for users
  const handleSearch = useCallback(async (searchTerm) => {
    setSearchQuery(searchTerm);

    if (searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      // Use new user search service (searches by email, phone, or name)
      // Only returns users with public profiles
      const results = await searchUsers(searchTerm);

      // Filter out current user
      const filtered = results.filter(u => u.id !== userId);

      setSearchResults(filtered);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [userId]);

  // Toggle user selection (for group chats)
  const toggleUserSelection = useCallback((user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      }
      return [...prev, user];
    });
  }, []);

  // Create DM with selected user
  const handleCreateDM = useCallback(async (targetUser) => {
    setLoading(true);
    setError('');

    try {
      // Validate that users can DM each other
      const canMessage = await checkCanDM(targetUser.id);

      if (!canMessage) {
        setError('You need to be contacts to start a conversation. Send them a contact request in the Contacts tab.');
        setLoading(false);
        return;
      }

      const chat = await createDirectMessageSecure(
        userId,
        targetUser.id,
        userName,
        targetUser.displayName || targetUser.email,
        user?.photoURL || userProfile?.photoURL,
        targetUser.photoURL
      );

      selectChat(chat.id);
      onClose();
    } catch (error) {
      console.error('Error creating DM:', error);
      setError(error.message || 'Failed to create conversation. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId, userName, selectChat, onClose, checkCanDM]);

  // Create group chat
  const handleCreateGroup = useCallback(async () => {
    if (!groupName.trim()) {
      setError('Please enter a group name');
      return;
    }

    if (selectedUsers.length === 0) {
      setError('Please select at least one person');
      return;
    }

    // Validate all selected users are contacts
    const contactIds = contacts.map(c => c.contactId);
    const nonContacts = selectedUsers.filter(u => !contactIds.includes(u.id));

    if (nonContacts.length > 0) {
      setError('Only contacts can be added to groups. Please remove non-contact users.');
      return;
    }

    // Check group chat limits
    const groupChatCount = chats.filter(c => c.type === 'group').length;
    const { allowed, reason } = canCreateGroupChat(groupChatCount);
    if (!allowed) {
      setError(reason);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const participantIds = selectedUsers.map(u => u.id);
      const chat = await createGroupChat(groupName.trim(), userId, participantIds, userName);

      selectChat(chat.id);
      onClose();
    } catch (error) {
      console.error('Error creating group:', error);
      setError('Failed to create group. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [groupName, selectedUsers, userId, canCreateGroupChat, selectChat, onClose, chats, contacts]);

  const groupUpgradePrompt = getUpgradePrompt('groupChats');

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#1c1c1e] rounded-2xl border border-white/10 max-w-md w-full max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-white font-semibold text-lg">New Chat</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex border-b border-white/10">
          <button
            onClick={() => setMode('dm')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              mode === 'dm'
                ? 'text-white border-b-2 border-[#06c]'
                : 'text-white/50 hover:text-white'
            }`}
          >
            Direct Message
          </button>
          <button
            onClick={() => setMode('group')}
            disabled={!canUseGroupChats}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              mode === 'group'
                ? 'text-white border-b-2 border-[#06c]'
                : 'text-white/50 hover:text-white'
            } ${!canUseGroupChats ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Group Chat
            {!canUseGroupChats && (
              <span className="ml-1 text-[10px] text-purple-400">PRO</span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Upgrade prompt for group chats */}
          {mode === 'group' && !canUseGroupChats && groupUpgradePrompt && (
            <div className="mb-4 p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
              <h3 className="text-purple-300 font-medium text-sm">{groupUpgradePrompt.title}</h3>
              <p className="text-purple-300/70 text-xs mt-1">{groupUpgradePrompt.description}</p>
            </div>
          )}

          {/* Group name input */}
          {mode === 'group' && canUseGroupChats && (
            <div className="mb-4">
              <label className="text-white/60 text-xs mb-1 block">Group Name</label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Enter group name..."
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#06c]/50"
                maxLength={50}
              />
            </div>
          )}

          {/* Selected users (group mode) */}
          {mode === 'group' && selectedUsers.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {selectedUsers.map(user => (
                <span
                  key={user.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 rounded-full text-white text-[13px]"
                >
                  {user.displayName || user.email}
                  <button
                    onClick={() => toggleUserSelection(user)}
                    className="w-4 h-4 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                  >
                    <svg className="w-2.5 h-2.5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Search input */}
          <div className="relative mb-4">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search by email..."
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#06c]/50"
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 py-2">
              <p className="text-red-400/80 text-[13px]">{error}</p>
            </div>
          )}

          {/* Search results */}
          <div className="max-h-60 overflow-y-auto">
            {searching ? (
              <div className="text-center py-12">
                <div className="w-5 h-5 border-2 border-white/10 border-t-white/40 rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-white/30 text-[13px]">Searching...</p>
              </div>
            ) : searchQuery.length < 2 ? (
              // Show contacts when no search query
              <div>
                {contacts.length > 0 ? (
                  <>
                    <div className="mb-3">
                      <h3 className="text-white/60 text-xs font-medium uppercase tracking-wider">
                        Your Contacts ({contacts.length})
                      </h3>
                    </div>
                    <div className="space-y-3">
                      {contacts.map(contact => (
                        <button
                          key={contact.id}
                          onClick={() => mode === 'dm' ? handleCreateDM({ id: contact.contactId, displayName: contact.contactName, email: contact.contactEmail }) : toggleUserSelection({ id: contact.contactId, displayName: contact.contactName, email: contact.contactEmail })}
                          disabled={loading}
                          className={`w-full flex items-center gap-4 py-3 px-1 transition-colors ${
                            mode === 'group' && selectedUsers.some(u => u.id === contact.contactId)
                              ? 'opacity-100'
                              : 'opacity-100 hover:opacity-80'
                          }`}
                        >
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#06c]/30 to-purple-500/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-medium text-base">
                              {(contact.nickname || contact.contactName || contact.contactEmail)?.[0]?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <p className="text-white font-medium text-[15px] truncate">
                              {contact.nickname || contact.contactName || 'Contact'}
                            </p>
                            <p className="text-white/40 text-[13px] truncate mt-0.5">{contact.contactEmail}</p>
                          </div>
                          {mode === 'group' && selectedUsers.some(u => u.id === contact.contactId) && (
                            <div className="w-5 h-5 rounded-full bg-[#06c] flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-white/40 text-[15px] mb-1">No contacts yet</p>
                    <p className="text-white/30 text-[13px]">Add contacts in the Contacts tab</p>
                  </div>
                )}
                <div className="mt-6 pt-4 border-t border-white/5">
                  <p className="text-white/30 text-[13px] text-center">
                    Search by email to find users
                  </p>
                </div>
              </div>
            ) : searchResults.length === 0 ? (
              <p className="text-white/40 text-[15px] text-center py-12">
                No users found
              </p>
            ) : (
              <div className="space-y-3">
                {searchResults.map(user => {
                  const isSelected = selectedUsers.some(u => u.id === user.id);
                  const isContact = contacts.some(c => c.contactId === user.id);

                  return (
                    <button
                      key={user.id}
                      onClick={() => {
                        if (mode === 'dm') {
                          if (isContact) {
                            handleCreateDM(user);
                          }
                        } else {
                          // For group mode, only allow selecting contacts
                          if (isContact) {
                            toggleUserSelection(user);
                          }
                        }
                      }}
                      disabled={loading || !isContact}
                      className={`w-full flex items-center gap-4 py-3 px-1 transition-opacity ${
                        isContact
                          ? 'opacity-100 hover:opacity-80'
                          : 'opacity-40 cursor-not-allowed'
                      }`}
                    >
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#06c]/30 to-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-medium text-base">
                          {(user.displayName || user.email)?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-white font-medium text-[15px] truncate">
                          {user.displayName || 'User'}
                        </p>
                        <p className={`text-[13px] truncate mt-0.5 ${isContact ? 'text-white/40' : 'text-white/30'}`}>
                          {user.email}
                        </p>
                      </div>
                      {!isContact && (
                        <span className="text-white/30 text-[13px] flex-shrink-0">Not a contact</span>
                      )}
                      {mode === 'group' && isSelected && isContact && (
                        <div className="w-5 h-5 rounded-full bg-[#06c] flex items-center justify-center flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer (group mode) */}
        {mode === 'group' && canUseGroupChats && (
          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleCreateGroup}
              disabled={loading || !groupName.trim() || selectedUsers.length === 0}
              className="w-full py-3 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white font-medium rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : `Create Group (${selectedUsers.length} selected)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
